import React, { useRef } from 'react';
import { Calendar, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

const EVENTS = [
  {
    tag: 'Outreach',
    date: '31 July 2026',
    title: 'Boychild Mentorship & Gala',
    location: 'Freedom Park Heritage Site & Museum, Pretoria',
    description:
      'Official launch of the Boychild Mentorship & Gala Movement 2026 \u2014 registration now open.',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/p4v4cy59_12.jpeg',
  },
  {
    tag: 'Programme',
    date: '30 July 2026',
    title: 'Boychild Mentorship Open Day',
    location: 'Freedom Park Heritage Site & Museum, Pretoria',
    description:
      'Public introduction of the Boychild Mentorship & Leadership Programme \u2014 meet mentors and register interest.',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/4f3vwm27_1333.jpg',
  },
  {
    tag: 'Workshop',
    date: '30 July 2026',
    title: 'Youth Leadership Workshop',
    location: 'Freedom Park Heritage Site & Museum, Pretoria',
    description:
      'Hands-on leadership and life-skills workshop for 100 boys aged 13–17.',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/foblayjn_1444.jpg',
  },
  {
    tag: 'Flagship',
    date: '26 June 2026',
    title: 'Boychild Mentorship',
    location: 'Freedom Park Heritage Site & Museum, Pretoria',
    description:
      'The flagship annual fundraising gala featuring a 32×32 Mentor matching ceremony and keynote speakers.',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/ws1oarf3_1555.jpg',
    featured: true,
  },
  {
    tag: 'Community',
    date: '26 June 2026',
    title: 'Boychild Mentorship',
    location: 'Freedom Park Heritage Site & Museum, Pretoria',
    description:
      'A 5km guided heritage walk honouring fathers, mentors and the boys they raise.',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/owp2j1r8_1666.jpg',
  },
  {
    tag: 'Milestone',
    date: '26 June 2026',
    title: 'Boychild Mentorship',
    location: 'Freedom Park Heritage Site & Museum, Pretoria',
    description:
      'Celebrating the graduation of our inaugural cohort of mentored young leaders.',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/up7kuyuq_17777.jpg',
  },
];

const GOLD = '#C9A23E';
const NAVY = '#1F3358';

const EventCard = ({ e }) => {
  const featured = e.featured;
  const card = (
    <article
      className={`shrink-0 w-[300px] sm:w-[330px] md:w-[360px] bg-white rounded-sm overflow-hidden snap-start card-lift relative ${
        featured
          ? 'border-2 border-[#C9A23E] shadow-[0_18px_45px_-12px_rgba(201,162,62,0.45)]'
          : 'border border-slate-100 shadow-sm'
      }`}
    >
      <div className="relative h-72 md:h-80 overflow-hidden bg-slate-100">
        <img
          src={e.image}
          alt={e.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
        />
      </div>

      <div className="p-7 pb-9">
        <div
          className="inline-flex items-center gap-2 text-[13px] font-semibold"
          style={{ color: GOLD }}
        >
          <Calendar size={14} strokeWidth={2.2} />
          <span>{e.date}</span>
        </div>

        <h3
          className="mt-4 text-2xl md:text-[26px] font-bold leading-snug"
          style={{ color: NAVY, fontFamily: "'Playfair Display', 'Quicksand', serif" }}
        >
          {e.title}
        </h3>

        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <MapPin size={14} style={{ color: GOLD }} />
          <span>{e.location}</span>
        </div>

        <p className="mt-5 text-slate-500 leading-7 text-[14.5px]">
          {e.description}
        </p>

        <div className="mt-5 pt-4 border-t border-slate-100 space-y-2">
          <p className="text-[13px] leading-6 text-slate-600">
            <span className="font-semibold" style={{ color: NAVY }}>Theme:</span>{' '}
            <em>&ldquo;The more our boys are ignored, the more our girls are in danger.&rdquo;</em>
          </p>
          <p className="text-[13px] leading-6 font-semibold" style={{ color: GOLD }}>
            &ldquo;Healing Boys. Protecting Generations.&rdquo;
          </p>
          <p className="text-[13px] leading-6 text-slate-600 pt-1">
            For more info, visit our{' '}
            <a
              href="https://whatsapp.com/channel/0029VbAwgy47dmeeh3vv2N0t"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="event-whatsapp-channel-link"
              className="font-semibold underline underline-offset-2 hover:text-[#25D366] transition-colors"
              style={{ color: '#25D366' }}
              onClick={(ev) => ev.stopPropagation()}
            >
              Channel
            </a>
            .
          </p>
        </div>
      </div>

      {/* Gold diamond marker at bottom */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
        <span
          className="block w-3 h-3 rotate-45"
          style={{ background: featured ? GOLD : '#E2C772' }}
        />
      </div>
    </article>
  );

  if (e.link) {
    return (
      <a
        href={e.link}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`event-link-${e.title}`}
        className="block"
      >
        {card}
      </a>
    );
  }
  return card;
};

const EventsTimeline = () => {
  const trackRef = useRef(null);
  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section className="py-20 md:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p
              className="uppercase text-xs tracking-[0.3em] font-bold mb-3"
              style={{ color: GOLD }}
            >
              Mark Your Calendar
            </p>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1]"
              style={{ color: NAVY, fontFamily: "'Playfair Display', 'Quicksand', serif" }}
            >
              Upcoming Events
            </h2>
            <div className="mt-5 flex items-center gap-2">
              <span className="w-12 h-0.5" style={{ background: GOLD }} />
              <span className="w-2 h-2 rotate-45" style={{ background: GOLD }} />
              <span className="w-12 h-0.5" style={{ background: GOLD }} />
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => scrollBy(-1)}
              aria-label="Previous events"
              className="w-12 h-12 rounded-full border border-slate-200 hover:border-[#C9A23E] hover:bg-[#C9A23E] hover:text-white grid place-items-center text-slate-700 transition-colors"
            >
              <ChevronLeft />
            </button>
            <button
              onClick={() => scrollBy(1)}
              aria-label="Next events"
              className="w-12 h-12 rounded-full border border-slate-200 hover:border-[#C9A23E] hover:bg-[#C9A23E] hover:text-white grid place-items-center text-slate-700 transition-colors"
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      </div>

      <div className="relative">
        <div
          ref={trackRef}
          className="overflow-x-auto snap-x snap-mandatory pb-8 px-6 md:px-12 scroll-smooth"
        >
          {/* Soft horizontal timeline line behind cards */}
          <div
            aria-hidden="true"
            className="absolute left-0 right-0 bottom-12 h-px"
            style={{
              backgroundImage:
                `repeating-linear-gradient(to right, ${GOLD} 0 6px, transparent 6px 14px)`,
              opacity: 0.35,
            }}
          />

          <ol className="flex gap-7 md:gap-8 w-max relative">
            {EVENTS.map((e, i) => (
              <li key={i} className="flex">
                <EventCard e={e} />
              </li>
            ))}
          </ol>
        </div>

        {/* Mobile arrows */}
        <div className="md:hidden flex justify-center gap-3 mt-4">
          <button
            onClick={() => scrollBy(-1)}
            aria-label="Previous events"
            className="w-11 h-11 rounded-full border border-slate-200 hover:border-[#C9A23E] hover:bg-[#C9A23E] hover:text-white grid place-items-center text-slate-700 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scrollBy(1)}
            aria-label="Next events"
            className="w-11 h-11 rounded-full border border-slate-200 hover:border-[#C9A23E] hover:bg-[#C9A23E] hover:text-white grid place-items-center text-slate-700 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default EventsTimeline;
